<template>
    <div class="pvd-grid__grid">
        <div class="pvd-grid__item pvd-grid__item--column-span-6-at-960">
            <ResultsRow
                isVisible="'true'"
                :labelId="'accrued-interest-id'"
                :resultId="'accrued-interest-result-id'"
                :labelText="clientConstants.RESULT_ACCRUED_INTEREST_LABEL"
                glyphSymbol="$"
                glyphAlign="left"
                :hasLink="true"
                :popoverType="clientConstants.AI_POPOVER_KEY"
                v-bind:resultValue="headerLevelData.accruedInterest"
            ></ResultsRow>
        </div>
        <div class="pvd-grid__item pvd-grid__item--column-span-6-at-960">
            <ResultsRow
                isVisible="'true'"
                :labelId="'total-cost-id'"
                :resultId="'total-cost-result-id'"
                :labelText="clientConstants.RESULT_TOTAL_COST_LABEL"
                glyphSymbol="$"
                glyphAlign="left"
                v-bind:resultValue="headerLevelData.invoicePrice"
            ></ResultsRow>
        </div>
        <div
            v-if="!calculateForYield"
            class="pvd-grid__item pvd-grid__item--column-span-6-at-960"
        >
            <ResultsRow
                :isVisible="!calculateForYield"
                :labelId="'calculated-price-id'"
                :resultId="'calculated-price-result-id'"
                :labelText="clientConstants.RESULT_PRICE_LABEL"
                v-bind:resultValue="headerLevelData.purchasePrice"
            ></ResultsRow>
        </div>
        <div
            v-if="calculateForYield"
            class="pvd-grid__item pvd-grid__item--column-span-6-at-960"
        >
            <ResultsRow
                :isVisible="calculateForYield"
                :labelId="'calculated-ytm-id'"
                :resultId="'calculated-ytm-result-id'"
                :labelText="clientConstants.RESULT_YIELD_TO_MATURITY_LABEL"
                glyphSymbol="%"
                glyphAlign="right"
                :hasLink="true"
                :popoverType="clientConstants.YTM_POPOVER_KEY"
                v-bind:resultValue="headerLevelData.yieldToMaturity"
            ></ResultsRow>
        </div>
        <div
            v-if="calculateForYield"
            class="pvd-grid__item pvd-grid__item--column-span-6-at-960"
        >
            <ResultsRow
                :isVisible="calculateForYield"
                :labelId="'calculated-ytc-id'"
                :resultId="'calculated-ytc-result-id'"
                :labelText="clientConstants.RESULT_YIELD_TO_CALL_LABEL"
                glyphSymbol="%"
                glyphAlign="right"
                :hasLink="true"
                :popoverType="clientConstants.YTC_POPOVER_KEY"
                v-bind:resultValue="headerLevelData.yieldToCall"
            ></ResultsRow>
        </div>
        <div class="pvd-grid__item pvd-grid__item--column-span-12">
            <highcharts id="highcharts"
                class="hc"
                :options="chartOptions"
                ref="chart"
            ></highcharts>
        </div>
    </div>
</template>
<script>
// Initial values for the header level
const headerLevelData = {
    accruedInterest: '',
    invoicePrice: '',
    purchasePrice: '',
    yieldToMaturity: '',
    callPrice: '',
    yieldToCall: '',
};
// Initial values for the chart
const yMin = 0;
const yMax = 100;
const xMin = 0;
const xMax = 100;

import axios from 'axios';
import ResultsRow from '../shared/ResultsRow.vue';
import { ClientConstants } from '../../client.constants';

export default {
    name: 'ResultsBlock',
    components: {
        ResultsRow,
    },
    mounted() {
        // use the root here to mount so that other components can call this.
        // this needs to be updated with post and input parameters
        this.$root.$on('getYieldPriceData', (reqInput) => {
            this.updateResponse(reqInput);
        });

        this.$root.$on('resetChart', () => {
            this.resetChartData();
            headerLevelData.accruedInterest = '';
            headerLevelData.invoicePrice = '';
            headerLevelData.purchasePrice = '';
            headerLevelData.yieldToMaturity = '';
            headerLevelData.callPrice = '';
            headerLevelData.yieldToCall = '';
            this.chartOptions.xAxis.min = 0;
            this.chartOptions.xAxis.max = 100;
            this.chartOptions.yAxis.min = 0;
            this.chartOptions.yAxis.max = 100;
        });
    },
    updated(){
        this.chartOptions.series[0].name = this.calculateForYield ? this.clientConstants.YIELD_TO_MATURITY_LEGEND : this.clientConstants.YIELD_LEGEND;
        this.chartOptions.series[1].showInLegend = this.calculateForYield;
    },
    props: {
        calculateForYield: Boolean,
    },
    methods: {
        resetChartData() {
            this.chartOptions.series.forEach((series) => {
                series.data = [];
            });
        },
        updateResponse(reqInput) {
            this.resetChartData();
            this.chartOptions.series[0].tooltip.pointFormat = this.calculateForYield
                ? this.clientConstants.YIELD_TO_MATURITY_CHART_HOVER
                : this.clientConstants.YIELD_CHART_HOVER;


            let config = {
                headers: {
                    'Content-Type': 'application/json;charset=UTF-8',
                },
            };
            axios
                .post(
                    '/prgw/digital/priceyieldcalc/api/calculations',
                    reqInput,
                    config
                )
                .then(async (response) => {
                    // update the header level fields
                    headerLevelData.accruedInterest =
                        response.data.accruedInterest;
                    headerLevelData.invoicePrice = response.data.invoicePrice;
                    headerLevelData.purchasePrice = response.data.purchasePrice;
                    headerLevelData.yieldToMaturity =
                        response.data.yieldToMaturity;
                    headerLevelData.yieldToCall = response.data.yieldToCall;
                    headerLevelData.callPrice = response.data.callPrice;

                    // Process the yield to maturity and yield to call for chart
                    const priceYieldToMaturityResp =
                        response.data.priceYieldToMaturity;
                    const priceYieldToCallResp = response.data.priceYieldToCall;

                    this.chartOptions.xAxis.min =
                        priceYieldToMaturityResp[0].price - 1;
                    this.chartOptions.xAxis.max =
                        priceYieldToMaturityResp[
                            priceYieldToMaturityResp.length - 1
                        ].price + 1;

                    const yieldToMaturityRespMaxYld =
                        priceYieldToMaturityResp[0].yield;
                    const yieldToMaturityRespMinYld =
                        priceYieldToMaturityResp[
                            priceYieldToMaturityResp.length - 1
                        ].yield;

                    // if yield to call is sent ,compare between yield to maturity and yield to call to determine the y axis min and max
                    if (priceYieldToCallResp != null) {
                        const yieldToCallRespMaxYld =
                            priceYieldToCallResp[0].yield;
                        const yieldToCallRespMinYld =
                            priceYieldToCallResp[
                                priceYieldToCallResp.length - 1
                            ].yield;
                        this.chartOptions.yAxis.max =
                            yieldToMaturityRespMaxYld > yieldToCallRespMaxYld
                                ? yieldToMaturityRespMaxYld + 1
                                : yieldToCallRespMaxYld + 1;
                        this.chartOptions.yAxis.min =
                            yieldToMaturityRespMinYld < yieldToCallRespMinYld
                                ? yieldToMaturityRespMinYld - 1
                                : yieldToCallRespMinYld - 1;
                    } else {
                        this.chartOptions.yAxis.max =
                            yieldToMaturityRespMaxYld + 1;
                        this.chartOptions.yAxis.min =
                            yieldToMaturityRespMinYld - 1;
                    }
                    priceYieldToMaturityResp.forEach((priceYield) => {
                        this.chartOptions.series[0].data.push({
                            x: priceYield.price,
                            y: priceYield.yield,
                        });
                    });
                    if (priceYieldToCallResp) {
                        priceYieldToCallResp.forEach((priceYield) => {
                            this.chartOptions.series[1].data.push({
                                x: priceYield.price,
                                y: priceYield.yield,
                            });
                        });
                    }
                })
                .catch((error) => {
                    console.log('error with data ' + error);
                });
        },
    },

    data() {
        return {
            clientConstants: ClientConstants,
            headerLevelData: headerLevelData,
            chartOptions: {
                chart: {
                    borderWidth: 1,
                    borderColor: '#A9A9A9',
                },
                series: [
                    {
                        name: this.calculateForYield ? ClientConstants.YIELD_TO_MATURITY_LEGEND : ClientConstants.YIELD_LEGEND,
                        type: 'line',
                        data: [],
                        tooltip: {
                            headerFormat: '',
                            pointFormat: this.calculateForYield
                                ? ClientConstants.YIELD_TO_MATURITY_CHART_HOVER
                                : ClientConstants.YIELD_CHART_HOVER,
                        },
                        showInLegend: true,
                    },
                    {
                        name: ClientConstants.YIELD_TO_CALL_LEGEND,
                        type: 'line',
                        data: [],
                        tooltip: {
                            headerFormat: '',
                            pointFormat: ClientConstants.YIELD_TO_CALL_CHART_HOVER,
                        },
                        showInLegend: true,
                    },
                ],
                title: { text: null },

                xAxis: {
                    min: xMin,
                    max: xMax,
                    title: {
                        text: 'Price',
                        style: {
                            fontFamily:
                                'Fidelity Sans", Helvetica, Arial, sans',
                            fontSize: '14px',
                            fontWeight: '700',
                            fontStyle: 'normal',
                            color: '#000000',
                        },
                    },
                    crosshair: {
                        width: 1,
                        color: 'green',
                    },
                    allowDecimals: true,
                    lineWidth: 2,
                    tickLength: 8,
                    tickColor: '#000000',
                    tickWidth: 1,
                    tickPosition: 'outside',

                    minorTickLength: 5,
                    minorTickWidth: 1,
                    minorTickInterval: (xMax - xMin) / 10,

                    gridLineColor: '#A9A9A9',
                    gridLineWidth: 1,

                    minorGridLineColor: '#A9A9A9',
                    minorGridLineWidth: 1,
                },
                yAxis: {
                    min: yMin,
                    max: yMax,
                    title: {
                        text: 'Yield %',
                        style: {
                            fontFamily:
                                'Fidelity Sans", Helvetica, Arial, sans',
                            fontSize: '12px',
                            fontWeight: '700',
                            fontStyle: 'normal',
                            color: '#000000',
                        },
                    },
                    crosshair: {
                        width: 1,
                        color: 'green',
                        dashStyle: 'shortdot',
                    },
                    allowDecimals: true,
                    lineColor: '#A9A9A9',
                    lineWidth: 2,

                    tickLength: 8,
                    tickColor: '#000000',
                    tickWidth: 1,
                    tickPosition: 'outside',

                    minorTickLength: 5,
                    minorTickWidth: 1,
                    minorTickInterval: (yMax - yMin) / 10,

                    gridLineColor: '#A9A9A9',
                    gridLineWidth: 1,

                    minorGridLineColor: '#A9A9A9',
                    minorGridLineWidth: 1,
                },
                credits: {
                    enabled: false,
                },
            },
        };
    },
};
</script>

<style lang="scss" scoped>
#highcharts {
    float: left;
    max-width:100%;
    min-width:90%;
}
</style>
